import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import MobileLayout from '../../components/MobileLayout';
import apiService from '../../../services/api';
import './MobileGenerate.css';

const IconChevron = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>;
const IconUser    = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>;
const IconMic     = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1a3 3 0 013 3v8a3 3 0 01-6 0V4a3 3 0 013-3z"/><path d="M19 10v2a7 7 0 01-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>;
const IconCheck   = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>;

const WAVE_BARS = [4, 8, 5, 11, 7, 9, 5, 12, 6, 8];

const MAX_CHARS = 1000;

export default function MobileGenerate() {
  const navigate = useNavigate();
  const location = useLocation();

  const [avatar, setAvatar]       = useState(null);
  const [voice, setVoice]         = useState(null);
  const [script, setScript]       = useState('');
  const [title, setTitle]         = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]         = useState('');
  const [success, setSuccess]     = useState(false);
  const [taskId, setTaskId]       = useState('');

  // Load saved selections from localStorage
  useEffect(() => {
    try {
      const a = localStorage.getItem('selectedAvatar');
      if (a) setAvatar(JSON.parse(a));
    } catch {}
    try {
      const v = localStorage.getItem('selectedVoice');
      if (v) setVoice(JSON.parse(v));
    } catch {}
  }, []);

  // Re-read selections when navigating back here (location.key changes on each navigation)
  useEffect(() => {
    try {
      const a = localStorage.getItem('selectedAvatar');
      setAvatar(a ? JSON.parse(a) : null);
    } catch {}
    try {
      const v = localStorage.getItem('selectedVoice');
      setVoice(v ? JSON.parse(v) : null);
    } catch {}
  }, [location.key]);

  const goSelectAvatar = () => {
    navigate('/m/select-avatar', { state: { from: '/m/generate' } });
  };

  const goSelectVoice = () => {
    navigate('/m/select-voice', { state: { from: '/m/generate' } });
  };

  const handleSubmit = async () => {
    if (!avatar) { setError('请先选择数字人形象'); return; }
    if (!voice)  { setError('请先选择声音'); return; }
    if (!script.trim()) { setError('请输入台词'); return; }

    setSubmitting(true);
    setError('');

    try {
      // Build generate payload — matches PC ProductionWorkflow field names
      const payload = {
        VirtualmanKey: avatar.key,
        TimbreKey:     voice.timbreKey || voice.key || voice.id,
        Text:          script.trim(),
      };
      if (title.trim()) payload.Title = title.trim();

      const result = await apiService.generateVideo(payload);

      if (result?.taskId) {
        setSuccess(true);
        setTaskId(result.taskId);
      } else if (result && result.code === 0) {
        setSuccess(true);
        setTaskId(result.data?.taskId || '');
      } else {
        setError(result?.message || result?.msg || '提交失败，请重试');
      }
    } catch (err) {
      setError(err.message || '网络错误，请重试');
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoTasks = () => {
    setSuccess(false);
    setScript('');
    navigate('/m/tasks');
  };

  const handleAgain = () => {
    setSuccess(false);
    setScript('');
    setTitle('');
    setTaskId('');
  };

  return (
    <MobileLayout title="生成视频" showBack>
      <div className="mgen-root">
        {/* ── Success state ── */}
        {success && (
          <>
            <div className="mgen-success">
              <div className="mgen-success-icon-wrap">
                <div className="mgen-success-check"><IconCheck /></div>
              </div>
              <div className="mgen-success-title">任务已提交！</div>
              <div className="mgen-success-sub">视频正在生成中，可前往「我的视频」查看进度</div>
              <div className="mgen-success-btns">
                <button className="mgen-success-btn mgen-success-btn--primary" onClick={handleGoTasks}>查看任务</button>
                <button className="mgen-success-btn mgen-success-btn--ghost" onClick={handleAgain}>再次生成</button>
              </div>
            </div>

            {/* Read-only summary card */}
            <div className="mgen-summary-card">
              <div className="mgen-summary-title">本次生成摘要</div>

              {/* Avatar row */}
              <div className="mgen-summary-row">
                <span className="mgen-summary-label">数字人形象</span>
                <div className="mgen-summary-val-wrap">
                  {avatar?.thumbnail && (
                    <img className="mgen-summary-thumb" src={avatar.thumbnail} alt={avatar.name} />
                  )}
                  <span className="mgen-summary-val">{avatar?.name || '—'}</span>
                </div>
              </div>

              <div className="mgen-summary-divider" />

              {/* Voice row */}
              <div className="mgen-summary-row">
                <span className="mgen-summary-label">音色</span>
                <div className="mgen-summary-val-wrap">
                  <div className="mgen-summary-wave">
                    {WAVE_BARS.map((h, i) => (
                      <div key={i} className="mgen-summary-wave-bar" style={{ height: `${h * 1.2}px` }} />
                    ))}
                  </div>
                  <span className="mgen-summary-val">{voice?.name || '—'}</span>
                </div>
              </div>

              <div className="mgen-summary-divider" />

              {/* Title row */}
              {title.trim() && (
                <>
                  <div className="mgen-summary-divider" />
                  <div className="mgen-summary-row">
                    <span className="mgen-summary-label">视频标题</span>
                    <span className="mgen-summary-val">{title.trim()}</span>
                  </div>
                </>
              )}

              <div className="mgen-summary-divider" />

              {/* Script row */}
              <div className="mgen-summary-row mgen-summary-row--script">
                <span className="mgen-summary-label">台词文案</span>
                <p className="mgen-summary-script">{script || '—'}</p>
              </div>
            </div>
          </>
        )}

        {/* ── Edit state ── */}
        {!success && (
          <>
            {/* Select avatar */}
            <div className="mgen-card">
              <div className="mgen-card-title">数字人形象</div>
              <div
                className={`mgen-select-row${avatar ? ' selected' : ''}`}
                onClick={goSelectAvatar}
              >
                <div className="mgen-select-thumb">
                  {avatar?.thumbnail
                    ? <img src={avatar.thumbnail} alt={avatar.name} />
                    : <IconUser />
                  }
                </div>
                <div className="mgen-select-info">
                  <div className="mgen-select-name">{avatar ? avatar.name : '选择数字人形象'}</div>
                  <div className="mgen-select-hint">{avatar ? '点击更换' : '从我的形象库中选择'}</div>
                </div>
                <div className="mgen-select-chevron"><IconChevron /></div>
              </div>
            </div>

            {/* Select voice */}
            <div className="mgen-card">
              <div className="mgen-card-title">声音</div>
              <div
                className={`mgen-select-row${voice ? ' selected' : ''}`}
                onClick={goSelectVoice}
              >
                <div className="mgen-select-thumb" style={{ background: voice ? '#ede9fe' : undefined }}>
                  {voice ? (
                    <div className="mgen-wave">
                      {WAVE_BARS.map((h, i) => (
                        <div key={i} className="mgen-wave-bar" style={{ height: `${h * 1.6}px` }} />
                      ))}
                    </div>
                  ) : (
                    <IconMic />
                  )}
                </div>
                <div className="mgen-select-info">
                  <div className="mgen-select-name">{voice ? voice.name : '选择声音'}</div>
                  <div className="mgen-select-hint">{voice ? '点击更换' : '从我的声音库中选择'}</div>
                </div>
                <div className="mgen-select-chevron"><IconChevron /></div>
              </div>
            </div>

            {/* Title */}
            <div className="mgen-card">
              <div className="mgen-card-title">视频标题 <span className="mgen-optional">可选</span></div>
              <input
                className="mgen-input"
                type="text"
                placeholder="为视频起个名字，方便在我的视频中识别"
                value={title}
                maxLength={100}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>

            {/* Script */}
            <div className="mgen-card">
              <div className="mgen-card-title">台词文案</div>
              <div className="mgen-textarea-wrap">
                <textarea
                  className="mgen-textarea"
                  placeholder="输入数字人说的台词，支持中英文..."
                  value={script}
                  maxLength={MAX_CHARS}
                  onChange={(e) => setScript(e.target.value)}
                />
                <span className="mgen-char-count">{script.length}/{MAX_CHARS}</span>
              </div>
            </div>

            {/* Error */}
            {error && <div className="mgen-error">{error}</div>}

            {/* Submit */}
            <div className="mgen-submit-wrap">
              <button className="mgen-submit" onClick={handleSubmit} disabled={submitting}>
                {submitting ? <span className="mgen-spin" /> : '开始生成'}
              </button>
            </div>
          </>
        )}
      </div>
    </MobileLayout>
  );
}
